import { getBaseLayer, getNewNodeChildren, getRootNode, isValidParent, pasteNodes } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { isDeletable, isMovable, RenderBuildNode } from "@builder/store/slices/BuilderSlice/builderMap";
import { BuilderState, HoveredOnNode } from "@builder/store/slices/BuilderSlice/types";
import { BaseLayer, ConditionalVariation, NodeSizes } from "@common/build/types";
import { PayloadAction } from "@reduxjs/toolkit";
import applyDefaultStyles from "./newNodeDefaultStyle";
import { InstalledBuilderElement } from "../../builderElementsInstalledApiSlice";

const removeNodeFromConditionalVariationsRecursive = (
    baseLayer: BaseLayer,
    nodeId: string,
    conditionalVariations: ConditionalVariation[]
): boolean => {
    let found = false;
    for (const cv of conditionalVariations) {
        if (cv.children?.includes(nodeId)) {
            cv.children = cv.children.filter(childId => childId !== nodeId);
            found = true;
        }
        if (cv.conditionalVariations) {
            const foundInNested = removeNodeFromConditionalVariationsRecursive(baseLayer, nodeId, cv.conditionalVariations);
            found = found || foundInNested;
        }
    }
    return found;
};

const deleteConditionalVariationNodes = (baseLayer: BaseLayer, conditionalVariations: ConditionalVariation[], parentNodeId: string): void => {
    for (const cv of conditionalVariations) {
        if (cv.children) {
            const childrenToDelete = [...cv.children];
            for (const childId of childrenToDelete) {
                deleteNodeHelper(baseLayer, childId);
            }
            cv.children = [];
        }
        if (cv.conditionalVariations) {
            deleteConditionalVariationNodes(baseLayer, cv.conditionalVariations, parentNodeId);
        }
    }
};

const deleteNodeHelper = (baseLayer: BaseLayer, nodeId: string): void => {
    const node = baseLayer.components[nodeId];
    if (!node) return;

    if (node.children) {
        const childrenToDelete = [...node.children];
        for (const child of childrenToDelete) {
            deleteNodeHelper(baseLayer, child);
        }
    }

    if (node.conditionalVariations) {
        deleteConditionalVariationNodes(baseLayer, node.conditionalVariations, node.id);
    }

    if (node.parent) {
        const parentNode = baseLayer.components[node.parent];
        if (parentNode) {
            let foundInCV = false;
            if (parentNode.conditionalVariations) {
                for (const cv of parentNode.conditionalVariations) {
                    if (cv.children?.includes(nodeId)) {
                        cv.children = cv.children.filter(childId => childId !== nodeId);
                        foundInCV = true;
                    }
                }
                const foundInNested = removeNodeFromConditionalVariationsRecursive(baseLayer, nodeId, parentNode.conditionalVariations);
                foundInCV = foundInCV || foundInNested;
            }
            if (!foundInCV && parentNode.children) {
                parentNode.children = parentNode.children.filter(childId => childId !== nodeId);
            }
        }
    }

    delete baseLayer.components[nodeId];
};

export const addNode = (state: BuilderState, action: PayloadAction<{ node: RenderBuildNode; index?: number }>) => {
    const baseLayer = getBaseLayer(state);
    const presets = action.payload.node.preset;
    const cvMode = state.conditionalVariationMode;

    if (presets) {
        const parentNode =
            baseLayer.components[state?.hoveredNode?.id || state?.selectedNode || baseLayer.root] || baseLayer.components[baseLayer.root]; //to resolve an edge case where the parent node is not found
        pasteNodes({ state, baseLayer, targetParent: parentNode, nodes: presets, newIndex: action.payload.index });
        return;
    }

    if (baseLayer) {
        if (action.payload.node.name === "slideout") {
            state.activeNode = action.payload.node.id;
        }
        const targetNodeSplit = (state.hoveredNode?.id || state.selectedNode)?.split("|");
        const [targetNodeId, targetNodeCVIndex] = targetNodeSplit?.[0] ? targetNodeSplit : [baseLayer.root];
        if (targetNodeId) {
            const newNode = action.payload.node;
            let currentTargetId: string | null | undefined = targetNodeId;
            while (currentTargetId && !isValidParent(baseLayer, newNode, currentTargetId, undefined, !!cvMode?.id)) {
                currentTargetId = baseLayer.components[currentTargetId].parent;
            }
            if (!currentTargetId) {
                currentTargetId = baseLayer.root;
            }

            const parentNode = baseLayer.components[currentTargetId];

            if (parentNode) {
                applyDefaultStyles(newNode, state, parentNode);
                baseLayer.components[newNode.id] = {
                    ...newNode,
                    parent: parentNode.id,
                    children: getNewNodeChildren(state, newNode)
                };

                if (cvMode?.id === parentNode.id && (typeof cvMode.index === "number" || targetNodeCVIndex)) {
                    const cvVariation = (
                        targetNodeCVIndex
                            ? parentNode.conditionalVariations?.[parseInt(targetNodeCVIndex)]
                            : typeof cvMode.index === "number"
                            ? parentNode.conditionalVariations?.[cvMode.index]
                            : undefined
                    ) as ConditionalVariation;
                    if (cvVariation) {
                        const cvChildren = cvVariation.children;
                        if (typeof action.payload.index === "number" && cvChildren && cvChildren.length > action.payload.index) {
                            cvChildren.splice(action.payload.index, 0, newNode.id);
                        } else {
                            cvVariation.children = [...(cvChildren || []), newNode.id];
                        }
                    }
                } else {
                    if (parentNode.children) {
                        if (typeof action.payload.index === "number" && parentNode.children.length > action.payload.index) {
                            parentNode.children.splice(action.payload.index, 0, newNode.id);
                        } else {
                            parentNode.children.push(newNode.id);
                        }
                    } else {
                        parentNode.children = [newNode.id];
                    }
                }

                state.selectedNode = newNode.id;
            }
        } else {
            const parentNode = baseLayer.components[baseLayer.root];
            const newNode = action.payload.node;
            applyDefaultStyles(newNode, state, parentNode);
            baseLayer.components[newNode.id] = {
                ...newNode,
                parent: parentNode.id,
                children: getNewNodeChildren(state, newNode)
            };

            if (parentNode) {
                if (cvMode?.id === parentNode.id && typeof cvMode.index === "number" && parentNode.conditionalVariations?.[cvMode.index]) {
                    const cvChildren = parentNode.conditionalVariations[cvMode.index].children;
                    if (typeof action.payload.index === "number" && cvChildren && cvChildren.length > action.payload.index) {
                        cvChildren.splice(action.payload.index, 0, newNode.id);
                    } else {
                        parentNode.conditionalVariations[cvMode.index].children = [...(cvChildren || []), newNode.id];
                    }
                } else {
                    if (parentNode.children) {
                        if (typeof action.payload.index === "number" && parentNode.children.length > action.payload.index) {
                            parentNode.children.splice(action.payload.index, 0, newNode.id);
                        } else {
                            parentNode.children.push(newNode.id);
                        }
                    } else {
                        parentNode.children = [newNode.id];
                    }

                    state.selectedNode = newNode.id;
                }
            }
        }
    }
};

export const deleteNode = (state: BuilderState, action: PayloadAction<string>) => {
    const baseLayer = getBaseLayer(state);
    const node = baseLayer.components[action.payload];
    if (!isDeletable(node)) {
        console.log("Cannot delete this node");
        return;
    }

    deleteNodeHelper(baseLayer, action.payload);

    if (node.parent) {
        const parent = baseLayer.components[node.parent];
        if (parent) {
            if (parent.conditionalVariations) {
                for (const cv of parent.conditionalVariations) {
                    cv.children = cv.children?.filter(childId => childId !== action.payload);
                }
            }
            if (parent.children) {
                parent.children = parent.children.filter(childId => childId !== action.payload);
            }
        }
    } else {
        const rootNode = getRootNode(baseLayer);
        if (rootNode.children) {
            rootNode.children = rootNode.children.filter(childId => childId !== action.payload);
        }
    }

    state.selectedNode = null;
    state.hoveredNode.id = null;
};

export const moveNode = (state: BuilderState, action: PayloadAction<{ nodeId: string; targetId: string; index?: number }>) => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer) {
        const node = baseLayer.components[action.payload.nodeId];
        const cvMode = state.conditionalVariationMode;

        if (!isMovable(node)) {
            console.log("Cannot move this node");
            return;
        }

        const findTargetNode = action.payload.targetId || baseLayer.components?.[action.payload.nodeId]?.parent || "";
        const [targetId, variationIndex] = findTargetNode.split("|");
        const targetNode = baseLayer.components[targetId];

        if (node && targetNode) {
            if (isValidParent(baseLayer, node, targetNode.id, true, !!cvMode?.id) && node.id !== baseLayer.root) {
                //Remove from current parent
                if (node.parent) {
                    const nodeParent = baseLayer.components[node.parent];
                    if (nodeParent.conditionalVariations) {
                        for (const cv of nodeParent.conditionalVariations) {
                            cv.children = cv.children?.filter(childId => childId !== node.id);
                        }
                    } else {
                        if (nodeParent.children) {
                            nodeParent.children = nodeParent.children.filter(childId => childId !== node.id);
                        }
                    }
                }

                //Add new parent
                node.parent = targetNode.id;

                //Add to new parent children
                if (targetNode.conditionalVariations && variationIndex) {
                    const cv = targetNode.conditionalVariations[parseInt(variationIndex)];
                    if (cv) {
                        if (cv.children) {
                            if (typeof action.payload.index === "number" && cv.children && cv.children.length > action.payload.index) {
                                cv.children.splice(action.payload.index, 0, node.id);
                            } else {
                                cv.children.push(node.id);
                            }
                        } else {
                            cv.children = [node.id];
                        }
                    }
                } else {
                    if (targetNode.children) {
                        if (typeof action.payload.index === "number" && targetNode.children.length > action.payload.index) {
                            targetNode.children.splice(action.payload.index, 0, node.id);
                        } else {
                            targetNode.children.push(node.id);
                        }
                    } else {
                        targetNode.children = [node.id];
                    }
                }
            }
        }
    }
};

export const selectNode = (state: BuilderState, action: PayloadAction<string | null>) => {
    state.selectedNode = action.payload;
};

export const setDraggedNode = (state: BuilderState, action: PayloadAction<string | null>) => {
    state.draggedNode = action.payload;
};

export const setDraggedUIComponent = (
    state: BuilderState,
    action: PayloadAction<{ key: string | null; dynamicElement?: InstalledBuilderElement } | null>
) => {
    state.draggedUIComponent = action.payload;
};

export const selectParent = (state: BuilderState) => {
    if (state.selectedNode) {
        const parentId = getBaseLayer(state).components[state.selectedNode].parent;
        if (parentId) {
            state.selectedNode = parentId;
        } else {
            state.selectedNode = null;
        }
    }
};

export const setHovered = (state: BuilderState, action: PayloadAction<Partial<HoveredOnNode>>) => {
    state.hoveredNode = { ...state.hoveredNode, ...action.payload };
};

export const updateChildrenSize = (state: BuilderState, action: PayloadAction<"column" | "row">) => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer && state.selectedNode) {
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode) {
            selectedNode.children?.forEach(childId => {
                const childNode = baseLayer.components[childId];
                if (childNode) {
                    if (action.payload === "column") {
                        if (childNode.nodeSize?.width === NodeSizes.FILL) {
                            childNode.attr = {
                                ...childNode.attr,
                                style: {
                                    ...childNode.attr?.style,
                                    width: "100%"
                                }
                            };
                        }
                        if (childNode.nodeSize?.height !== NodeSizes.FILL) {
                            delete childNode.attr?.style?.flexGrow;
                        } else {
                            delete childNode.attr?.style?.height;
                        }
                    } else {
                        if (childNode.nodeSize?.height === NodeSizes.FILL) {
                            childNode.attr = {
                                ...childNode.attr,
                                style: {
                                    ...childNode.attr?.style,
                                    height: "100%"
                                }
                            };
                        }
                        if (childNode.nodeSize?.width !== NodeSizes.FILL) {
                            delete childNode.attr?.style?.flexGrow;
                        } else {
                            delete childNode.attr?.style?.width;
                        }
                    }
                }
            });
        }
    }
};
